import { apiClient } from './client';
import type {
  CarFilters,
  CarsResponse,
  CarResponse,
  CarsByBatteryResponse,
  CarStatsResponse,
  MakesResponse,
  ModelsForMakeResponse
} from '../types/car';

/**
 * Car API Client
 * Handles all car-related API requests
 */

/**
 * Get all cars with filtering and pagination
 */
export const getCars = async (filters: CarFilters = {}): Promise<CarsResponse> => {
  const params = new URLSearchParams();
  
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.make) params.append('make', filters.make);
  if (filters.model) params.append('model', filters.model);
  if (filters.year) params.append('year', filters.year.toString());
  if (filters.batterySize) params.append('batterySize', filters.batterySize);
  if (filters.search) params.append('search', filters.search);

  const response = await apiClient.get<CarsResponse>(
    `/cars?${params.toString()}`
  );
  return response.data;
};

/**
 * Get car by ID
 */
export const getCarById = async (id: number): Promise<CarResponse> => {
  const response = await apiClient.get<CarResponse>(`/cars/${id}`);
  return response.data;
};

/**
 * Search cars by battery size
 */
export const getCarsByBatterySize = async (
  batterySize: string,
  page = 1,
  limit = 50
): Promise<CarsByBatteryResponse> => {
  const response = await apiClient.get<CarsByBatteryResponse>(
    `/cars/search/battery/${encodeURIComponent(batterySize)}?page=${page}&limit=${limit}`
  );
  return response.data;
};

/**
 * Get all unique battery sizes
 */
export const getBatterySizes = async (): Promise<string[]> => {
  const response = await apiClient.get<MakesResponse>('/cars/batteries/sizes');
  return response.data.data;
};

/**
 * Get all unique car makes
 */
export const getMakes = async (): Promise<string[]> => {
  const response = await apiClient.get<MakesResponse>('/cars/makes');
  return response.data.data;
};

/**
 * Get all models for a specific make
 */
export const getModelsForMake = async (make: string): Promise<string[]> => {
  const response = await apiClient.get<ModelsForMakeResponse>(
    `/cars/makes/${encodeURIComponent(make)}/models`
  );
  return response.data.data.models;
};

/**
 * Get car statistics
 */
export const getCarStats = async (): Promise<CarStatsResponse> => {
  const response = await apiClient.get<CarStatsResponse>('/cars/stats');
  return response.data;
};

/**
 * Trigger car data sync from Google Sheets
 */
export const triggerCarSync = async (): Promise<{ success: boolean; data: { status: string; message: string } }> => {
  const response = await apiClient.post('/sync/cars');
  return response.data;
};
